/**
 * EquipmentDocumentViewer Component
 * Displays documents associated with specific equipment
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Chip,
  Grid,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Description as DocumentIcon,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Build as BuildIcon,
  Security as SecurityIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon
} from '@mui/icons-material';
import {
  documentsService,
  Document,
  DocumentCategory
} from '../../services/documentsService';

interface EquipmentDocumentViewerProps {
  equipmentId: number;
  equipmentName: string;
  equipmentKksCode: string;
  onUploadDocument?: () => void;
}

const EquipmentDocumentViewer: React.FC<EquipmentDocumentViewerProps> = ({
  equipmentId,
  equipmentName,
  equipmentKksCode,
  onUploadDocument
}) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadEquipmentDocuments();
  }, [equipmentId]);

  const loadEquipmentDocuments = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const docs = await documentsService.getEquipmentDocuments(equipmentId);
      // Ensure we always set an array, even if the API returns something unexpected
      setDocuments(Array.isArray(docs) ? docs : []);
    } catch (err) {
      console.error('Failed to load equipment documents:', err);
      setError('Failed to load equipment documents');
      // Set empty array on error to prevent reduce errors
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = async (doc: Document) => {
    try {
      const blob = await documentsService.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${doc.document_number}_${doc.title}.${doc.file_extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download document:', err);
      setError('Failed to download document');
    }
  };

  const viewDocumentDetails = (doc: Document) => {
    setSelectedDocument(doc);
    setDetailsOpen(true);
  };

  const getFileIcon = (extension: string) => {
    switch (extension?.toLowerCase()) {
      case 'pdf':
        return <PdfIcon sx={{ color: '#d32f2f' }} />;
      case 'doc':
      case 'docx':
        return <DocumentIcon sx={{ color: '#1976d2' }} />;
      default:
        return <FileIcon sx={{ color: '#757575' }} />;
    }
  };

  const getCategoryIcon = (categoryName: string) => {
    switch (categoryName?.toLowerCase()) {
      case 'maintenance documentation':
        return <BuildIcon sx={{ color: '#f57c00' }} />;
      case 'safety documentation':
        return <SecurityIcon sx={{ color: '#d32f2f' }} />;
      case 'operational procedures':
        return <AssignmentIcon sx={{ color: '#1976d2' }} />;
      case 'training materials':
        return <SchoolIcon sx={{ color: '#388e3c' }} />;
      default:
        return <DocumentIcon sx={{ color: '#757575' }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'success';
      case 'approved':
        return 'info';
      case 'under_review':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const groupDocumentsByCategory = (docs: Document[]) => {
    // Extra defensive check - ensure docs is always an array
    if (!Array.isArray(docs)) {
      console.error('groupDocumentsByCategory received non-array:', docs);
      return {};
    }
    
    const grouped = docs.reduce((acc, doc) => {
      const category = doc.category_name || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(doc);
      return acc;
    }, {} as Record<string, Document[]>);

    // Sort documents within each category by type and then by name
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const typeComparison = a.document_type_name.localeCompare(b.document_type_name);
        if (typeComparison !== 0) return typeComparison;
        return a.title.localeCompare(b.title);
      });
    });

    return grouped;
  };

  // Ensure documents is always an array before grouping
  const safeDocuments = Array.isArray(documents) ? documents : [];
  const documentsByCategory = groupDocumentsByCategory(safeDocuments);
  const hasDocuments = safeDocuments && safeDocuments.length > 0;
  const categoryCount = Object.keys(documentsByCategory).length;

  if (loading) {
    return (
      <Card sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={loadEquipmentDocuments}>
          Retry
        </Button>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Equipment Documents
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {equipmentKksCode} - {equipmentName}
            </Typography>
            <Chip 
              label={`${documents.length} documents`} 
              size="small" 
              sx={{ mt: 1 }} 
            />
          </Box>
          
          {onUploadDocument && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onUploadDocument}
              size="small"
            >
              Upload Document
            </Button>
          )}
        </Box>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!hasDocuments ? (
        <Card sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No documents found for this equipment.
          </Typography>
          {onUploadDocument && (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={onUploadDocument}
              sx={{ mt: 1 }}
            >
              Upload First Document
            </Button>
          )}
        </Card>
      ) : (
        <Grid container spacing={2}>
          {Object.keys(documentsByCategory).map((categoryName) => {
            const categoryDocs = documentsByCategory[categoryName];
            return (
            <Grid item xs={12} key={categoryName}>
              <Card>
                <Box sx={{ p: 2, bgcolor: 'grey.50', display: 'flex', alignItems: 'center', gap: 1 }}>
                  {getCategoryIcon(categoryName)}
                  <Typography variant="h6">
                    {categoryName}
                  </Typography>
                  <Chip 
                    label={categoryDocs.length} 
                    size="small" 
                    color="primary" 
                  />
                </Box>
                
                <List sx={{ pt: 0 }}>
                  {categoryDocs.map((doc, index) => (
                    <React.Fragment key={doc.id}>
                      {index > 0 && <Divider />}
                      <ListItem>
                        <ListItemIcon>
                          {getFileIcon(doc.file_extension)}
                        </ListItemIcon>
                        
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight="medium">
                                {doc.title}
                              </Typography>
                              <Chip 
                                label={doc.status} 
                                size="small" 
                                color={getStatusColor(doc.status) as any}
                              />
                              {doc.is_expired && (
                                <Chip 
                                  label="Expired" 
                                  size="small" 
                                  color="error" 
                                />
                              )}
                              {doc.needs_review && (
                                <Chip 
                                  label="Needs Review" 
                                  size="small" 
                                  color="warning" 
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                {doc.document_type_name} • Version {doc.version} • {doc.document_number}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Created {new Date(doc.created_date).toLocaleDateString()} by {doc.created_by_name}
                              </Typography>
                              {doc.description && (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                  {doc.description.length > 100 
                                    ? `${doc.description.substring(0, 100)}...` 
                                    : doc.description
                                  }
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        
                        <ListItemSecondaryAction>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="View Details">
                              <IconButton
                                edge="end"
                                onClick={() => viewDocumentDetails(doc)}
                                size="small"
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download">
                              <IconButton
                                edge="end"
                                onClick={() => downloadDocument(doc)}
                                size="small"
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </ListItemSecondaryAction>
                      </ListItem>
                    </React.Fragment>
                  ))}
                                 </List>
               </Card>
             </Grid>
           );
           })}
         </Grid>
      )}

      {/* Document Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedDocument && getFileIcon(selectedDocument.file_extension)}
            <Typography variant="h6">
              {selectedDocument?.title}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {selectedDocument && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Document Information</Typography>
                <Typography variant="body2"><strong>Document Number:</strong> {selectedDocument.document_number}</Typography>
                <Typography variant="body2"><strong>Type:</strong> {selectedDocument.document_type_name}</Typography>
                <Typography variant="body2"><strong>Category:</strong> {selectedDocument.category_name}</Typography>
                <Typography variant="body2"><strong>Version:</strong> {selectedDocument.version}</Typography>
                <Typography variant="body2"><strong>Status:</strong> {selectedDocument.status}</Typography>
                <Typography variant="body2"><strong>Access Level:</strong> {selectedDocument.access_level}</Typography>
                <Typography variant="body2"><strong>File Size:</strong> {selectedDocument.file_size_display}</Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>Dates & People</Typography>
                <Typography variant="body2"><strong>Created:</strong> {new Date(selectedDocument.created_date).toLocaleDateString()}</Typography>
                <Typography variant="body2"><strong>Created By:</strong> {selectedDocument.created_by_name}</Typography>
                {selectedDocument.approved_by_name && (
                  <>
                    <Typography variant="body2"><strong>Approved By:</strong> {selectedDocument.approved_by_name}</Typography>
                    <Typography variant="body2"><strong>Approved:</strong> {selectedDocument.approved_date ? new Date(selectedDocument.approved_date).toLocaleDateString() : 'N/A'}</Typography>
                  </>
                )}
                {selectedDocument.effective_date && (
                  <Typography variant="body2"><strong>Effective:</strong> {new Date(selectedDocument.effective_date).toLocaleDateString()}</Typography>
                )}
                {selectedDocument.expiry_date && (
                  <Typography variant="body2"><strong>Expires:</strong> {new Date(selectedDocument.expiry_date).toLocaleDateString()}</Typography>
                )}
              </Grid>
              
              {selectedDocument.description && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Description</Typography>
                  <Typography variant="body2">{selectedDocument.description}</Typography>
                </Grid>
              )}
              
              {selectedDocument.keywords && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Keywords</Typography>
                  <Typography variant="body2">{selectedDocument.keywords}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            Close
          </Button>
          {selectedDocument && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                downloadDocument(selectedDocument);
                setDetailsOpen(false);
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EquipmentDocumentViewer; 